import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import {
  getModerationSummary,
  listModerationQueue,
  type InternalCorrectionStatus,
  type ModerationQueueFilters,
} from '../../lib/moderationApi';
import {
  ModerationQueueFiltersForm,
  selectableQueueStatuses,
} from './ModerationQueueFilters';
import {
  ErrorMessage,
  StatusBadge,
  secondaryButtonClass,
} from './ModerationUi';

const filtersFromSearch = (search: string): ModerationQueueFilters => {
  const params = new URLSearchParams(search);
  const page = Number(params.get('page') || '1');
  const requestedStatus = params.get('status') ?? '';
  const status = selectableQueueStatuses.some(
    (option) => option.value === requestedStatus
  )
    ? (requestedStatus as InternalCorrectionStatus | '')
    : '';
  return {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    status,
    gameDate: params.get('gameDate') ?? '',
    replay: params.get('replay') ?? '',
    squad: params.get('squad') ?? '',
  };
};

export function ModerationQueue({
  locationSearch,
  onNavigate,
  onAuthorizationFailure,
}: {
  locationSearch: string;
  onNavigate: (path: string) => void;
  onAuthorizationFailure: () => void;
}): JSX.Element {
  const filters = useMemo(() => filtersFromSearch(locationSearch), [locationSearch]);

  const queue = useQuery({
    queryKey: ['moderation', 'queue', filters],
    queryFn: () => listModerationQueue(filters),
  });
  const awaitingRecalc = useQuery({
    queryKey: ['moderation', 'awaiting-recalc-count'],
    queryFn: getModerationSummary,
  });
  useEffect(() => {
    if (
      queue.error instanceof ApiError &&
      (queue.error.status === 401 || queue.error.status === 403)
    ) {
      onAuthorizationFailure();
    }
  }, [onAuthorizationFailure, queue.error]);

  const applyFilters = (draft: ModerationQueueFilters) => {
    const params = new URLSearchParams();
    if (draft.status) params.set('status', draft.status);
    if (draft.gameDate) params.set('gameDate', draft.gameDate);
    if (draft.replay) params.set('replay', draft.replay);
    if (draft.squad) params.set('squad', draft.squad);
    onNavigate(`/moderation/manual-corrections${params.size ? `?${params}` : ''}`);
  };

  const goToPage = (page: number) => {
    const params = new URLSearchParams(locationSearch);
    params.set('page', String(page));
    onNavigate(`/moderation/manual-corrections?${params}`);
  };

  return (
    <main className="mx-auto w-full max-w-7xl space-y-5 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Очередь ручных корректировок</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Проверка заявки не запускает пересчёт статистики.
          </p>
        </div>
        <div className="rounded-md border border-border bg-card px-4 py-3 text-sm">
          Ожидают штатного пересчёта:{' '}
          <strong>{awaitingRecalc.data?.awaitingRecalcCount ?? '—'}</strong>
        </div>
      </div>

      <ModerationQueueFiltersForm filters={filters} onApply={applyFilters} />

      {queue.isLoading ? (
        <div role="status" className="rounded-lg border border-border bg-card p-8 text-center">
          Загрузка очереди…
        </div>
      ) : queue.error ? (
        <div className="space-y-3">
          <ErrorMessage error={queue.error} />
          <button className={secondaryButtonClass} type="button" onClick={() => void queue.refetch()}>
            Повторить
          </button>
        </div>
      ) : queue.data?.results.length === 0 ? (
        <div role="status" className="rounded-lg border border-border bg-card p-8 text-center">
          Заявок по выбранным фильтрам нет.
        </div>
      ) : (
        <ul className="space-y-3">
          {queue.data?.results.map((item) => (
            <li key={item.public_id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={item.status} />
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.created_at).toLocaleString('ru-RU')}
                    </span>
                  </div>
                  <a
                    className="block break-all font-medium text-primary underline underline-offset-2"
                    href={item.replay_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.replay_name}
                  </a>
                  <p className="text-sm text-muted-foreground">
                    {item.claimed_killer_name}
                    {item.claimed_killer_squad ? ` [${item.claimed_killer_squad}]` : null}
                    {' → '}
                    {item.claimed_victim_name}
                    {item.claimed_victim_squad ? ` [${item.claimed_victim_squad}]` : null}
                  </p>
                </div>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() =>
                    onNavigate(`/moderation/manual-corrections/${item.public_id}`)
                  }
                >
                  Открыть
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {queue.data && queue.data.count > 0 ? (
        <nav aria-label="Пагинация очереди" className="flex items-center justify-between">
          <button
            type="button"
            className={secondaryButtonClass}
            disabled={!queue.data.previous}
            onClick={() => goToPage(Math.max(1, (filters.page ?? 1) - 1))}
          >
            Назад
          </button>
          <span className="text-sm text-muted-foreground">Страница {filters.page ?? 1}</span>
          <button
            type="button"
            className={secondaryButtonClass}
            disabled={!queue.data.next}
            onClick={() => goToPage((filters.page ?? 1) + 1)}
          >
            Далее
          </button>
        </nav>
      ) : null}
    </main>
  );
}
